import { ref, computed } from 'vue';

import { defineStore } from 'pinia';

import { useRouter } from 'vue-router';

import ApiBaseStations from '@/api/ApiBaseStations';
import Utils from '@/api/Utils';

import { getAxiosErrorStatus, logError } from '@/api/ApiHelpers';

import {
	normalizeBaseStationStatus,
	normalizeBaseStationOnlineStatus,
	normalizeBaseStationLiveStatus,
} from '@/utils/baseStationsUtils';

import { showErrorMessage } from '@/components/common/errorMessage';

import type { SelectOption } from '@/components/common/types/selectOptionTypes';

import { useModuleStatusesStore } from '@/stores/moduleStatuses';
import { useEventsStore } from '@/stores/events';
import { useGnssVisibilityStore, useGnssPerGridpointStore } from '@/stores/gnssVisibilityStore';
import { useSnrStore, useMultipathStore, useResidualsStore } from '@/stores/snrMultipathStore';
import { usePaasStddevStore } from '@/stores/paasStddevStore';
import { useMapGssOverlayStore } from '@/stores/mapGssOverlayStore';
import { useLicenseStore } from '@/stores/licenseStore';
import { useBaselinesStore } from '@/stores/baselinesStore';
import { useUserStore } from '@/stores/user';
import { useSubNetworksStore } from '@/stores/subNetworksStore';

import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type { MapMode } from '@/components/map/consts/mapFeatureConsts';
import type {
	GeoFencingRaw,
	MapBaseStationStored,
	MapDataPayload,
	ProfileGeoFencingRaw,
	ProfileGeoFencingStored,
	MapBaseStationRaw,
	SubNetworkRaw,
} from '@/api/ApiTypes';
import type { VRSParticipantRoverRaw } from '@/utils/types/baseStationsTypes';


/*
base station data example:
{
	"id": 68,
	"label": "Волоколамск",
	"status": "working",
	"status_online": "no_data",
	"status_live": "no_live_data",
	"deleted": false,
	"updating": false,
	"x_coord": 2891192158,
	"y_coord": 2097395825,
	"z_coord": 5266704614,
	"reliable_coords": true,
	"ant": "TPSCR.G5        TPSH",
	"ant_detected": "",
	"ant_reference_point": "ARP",
	"ant_reference_point_detected": "NA",
	"protocol": "ntrip_caster",
	"protocol_secure": false,
	"b_coord": 56.03369608422669,
	"l_coord": 35.9587547539627,
	"mntpnt_int": "50V2",
	"mntpnt_src": "50V2",
	"display_address": "ntrip2://nafnaf:nifnif@@185.187.90.22:2104/50V2",
	"radius": 20000,
	"heartbeat_error_code": "",
	"heartbeat_error_message": "",
	"include_in_near": true,
	"include_in_vrs": true,
	"allow_ant_detection": false,
	"dump_caster_input_raw_data": false,
	"info": ""
}
*/

/*
rovers data example:
{
	"rovers": [
		{
			"service": "vrs",
			"username": "rover",
			"name": "rover0",
			"status": "active",
			"b_coord": 55.92482905499999,
			"l_coord": 38.69524315166666,
			"connected_base_station": {
				"id": 83,
				"service": "vrs",
				"b_coord": 55.6245,
				"l_coord": 37.7705,
				"radius": 12000
			},
			"additional_data": "",
			"is_new": false,
			"sub_network_id": 20,
			"rover_session_id": 100
		},
		{
			"service": "near",
			"username": "rover",
			"name": "rover1",
			"status": "failed",
			"b_coord": 55.7109,
			"l_coord": 38.502,
			"connected_base_station": null,
			"additional_data": "{\"users\":{\"rover\":2,\"admin\":1}}",
			"is_new": true,
			"sub_network_id": null,
			"rover_session_id": null
		},
		{
			"service": "vrs",
			"username": "rover",
			"name": "rover2",
			"status": "active",
			"b_coord": 55.82482905499999,
			"l_coord": 38.30524315166666,
			"connected_base_station": {
				"id": 83,
				"service": "vrs",
				"b_coord": 55.6245,
				"l_coord": 37.7705,
				"radius": 12000
			},
			"additional_data": "",
			"is_new": false,
			"sub_network_id": 20,
			"rover_session_id": 111
		}
	]
}
*/

/*
profiles data example:
{
	"profiles": [
		{
			"username": "rover",
			"geo_fencing_b_coord": 55.0,
			"geo_fencing_l_coord": 37.0,
			"geo_fencing_radius": 10.0
		}
	]
}
*/


export const SUB_NETWORK_OPTIONAL_MODES: readonly MapMode[] = [
	MAP_MODE.main,
	MAP_MODE.subNetworks,
	MAP_MODE.coverage,
	MAP_MODE.gnss,
];

export const SUB_NETWORK_STRICT_MODES: readonly MapMode[] = [
	MAP_MODE.gnssPerGridpoint,
	MAP_MODE.residuals,
];

/*
Режимы, для которых отображается легенда GNSS
*/
export const GNSS_MODES: readonly MapMode[] = [
	MAP_MODE.gnss,
	MAP_MODE.gnssPerGridpoint,
	MAP_MODE.coverage,
	MAP_MODE.snr,
	MAP_MODE.multipath,
	MAP_MODE.residuals,
];

export const FILL_COVERAGE_AREA_MODES: readonly MapMode[] = [
	MAP_MODE.main,
	MAP_MODE.gnssPerGridpoint,
];

type ChangeStatusResponse = {
	status: string;
	errors?: unknown;
};


const isResidualsVisibilityMode = (modeValue: MapMode): boolean => (
	modeValue === MAP_MODE.residuals || modeValue === MAP_MODE.coverage
);


export const useBaseStationsStore = defineStore('baseStations', () => {
	const mode = ref<MapMode>(MAP_MODE.main);
	const items = ref<MapBaseStationStored[]>([]);
	const rovers = ref<VRSParticipantRoverRaw[]>([]);
	const maxBaseStationsCount = ref<number | null>(null);
	const geoFencing = ref<GeoFencingRaw | null>(null);
	const profiles = ref<ProfileGeoFencingStored[]>([]);
	const isLoading = ref(true);
	const autoUpdatesAllowed = ref(true);

	const subNetworksStore = useSubNetworksStore();

	const mountpointsSelectOptions = computed((): SelectOption<string>[] => [...items.value
		.flatMap(item => item.mntpnt_int != null
			? [{ value: item.mntpnt_int, label: item.mntpnt_int }]
			: [])]
		.sort(Utils.objectFieldMultiSorter('label')));

	const mountpointsSelectCurrentSubNetworkOptions = computed((): SelectOption<string>[] => {
		if (subNetworksStore.subNetworkSelectedId == null) {
			return mountpointsSelectOptions.value;
		}
		const selectedSubNetworkId = subNetworksStore.subNetworkSelectedId;
		return [...items.value
			.filter(baseStation => baseStation.subNetworkIds.includes(selectedSubNetworkId))
			.flatMap(baseStation => baseStation.mntpnt_int != null
				? [{ value: baseStation.mntpnt_int, label: baseStation.mntpnt_int }]
				: [])]
			.sort(Utils.objectFieldMultiSorter('label'));
	});

	const userStore = useUserStore();
	const baselinesStore = useBaselinesStore();
	const update = (
		subNetworksValue: SubNetworkRaw[],
		itemsValue: MapBaseStationRaw[],
		roversValue: VRSParticipantRoverRaw[],
		maxBaseStationsCountValue: number | null,
		geoFencingValue: GeoFencingRaw | null,
		profilesValue: ProfileGeoFencingRaw[],
	): void => {
		subNetworksStore.updateSubNetworks(subNetworksValue);
		items.value = itemsValue.map((baseStation): MapBaseStationStored => ({
			...baseStation,
			status: normalizeBaseStationStatus(baseStation.status),
			status_online: normalizeBaseStationOnlineStatus(baseStation.status_online),
			status_live: normalizeBaseStationLiveStatus(
				'status_live' in baseStation ? baseStation.status_live : undefined,
			),
			baselineIds: baselinesStore.baselinesPerBaseStations.get(baseStation.id) ?? [],
			subNetworkIds: subNetworksStore.subNetworksPerBaseStations.get(baseStation.id) ?? [],
		}));
		rovers.value = roversValue;
		maxBaseStationsCount.value = maxBaseStationsCountValue;
		geoFencing.value = geoFencingValue;
		profiles.value = profilesValue.map(profile => ({
			username: profile.username,
			lat: profile.geo_fencing_b_coord,
			lon: profile.geo_fencing_l_coord,
			radius: (profile.geo_fencing_radius ?? 0) * 1000,
		}));
		isLoading.value = false;
	};

	const setAutoUpdates = (allow: boolean): void => {
		isLoading.value = allow;
		autoUpdatesAllowed.value = allow;
	};

	const handleBaseStationChangeStatus = (onSuccess?: (responseData: ChangeStatusResponse) => void) => (
		responseData: ChangeStatusResponse,
	): void => {
		if (responseData.status === 'ok') {
			if (Utils.isFunction(onSuccess)) {
				onSuccess(responseData);
			}
		} else {
			showErrorMessage(responseData.errors);
		}
	};

	const changeStatuses = (keys: number[], action: string, onSuccess?: (responseData: ChangeStatusResponse) => void): void => {
		items.value.forEach(item => {
			if (keys.includes(item.id)) {
				if (action === 'delete') {
					item.deleted = true;
					item.status = 'deleted';
				} else {
					item.deleted = false;
					item.status = 'updating';
				}
			}
		});
		const firstKey = keys[0];
		if (keys.length > 1) {
			ApiBaseStations.baseStationChangeGroupStatus(keys, action, handleBaseStationChangeStatus(onSuccess));
		} else if (firstKey !== undefined) {
			ApiBaseStations.baseStationChangeStatus(firstKey, action, handleBaseStationChangeStatus(onSuccess));
		}
	};

	const router = useRouter();
	const handleBaseStationsList = (responseData: MapDataPayload): void => {
		const response = responseData as Partial<MapDataPayload> & {
			status?: string;
			errors?: unknown;
		};
		if (
			!Array.isArray(response.sub_networks)
			|| !Array.isArray(response.base_stations)
			|| !Array.isArray(response.rovers)
			|| response.map_settings == null
			|| !Array.isArray(response.profiles)
		) {
			setAutoUpdates(false);
			console.error('invalid map data response', {
				status: response.status,
				responseKeys: Object.keys(response),
			});
			if (response.errors != null) {
				showErrorMessage(response.errors);
			}
			return;
		}
		update(
			response.sub_networks,
			response.base_stations,
			response.rovers,
			response.map_settings.maxBaseStationsCount ?? null,
			response.map_settings.geoFencing ?? null,
			response.profiles,
		);
		if (router.currentRoute.value.name === 'home-public') {
			router.push({ name: 'home-map' });
		}
	};

	const handleBaseStationsListError = (error: unknown): void => {
		if (getAxiosErrorStatus(error) === 401) {
			setAutoUpdates(false);
			update([], [], [], null, null, []);
			router.push({ name: 'home-public' });
		} else {
			logError(error);
		}
	};

	const baseStationRefreshList = (): void => {
		setAutoUpdates(true);
		ApiBaseStations.mapGetData(handleBaseStationsList, handleBaseStationsListError);
	};

	const moduleStatuses = useModuleStatusesStore();
	const events = useEventsStore();
	const gnssVisibilityStore = useGnssVisibilityStore();
	const gnssPerGridpointStore = useGnssPerGridpointStore();
	const snrStore = useSnrStore();
	const multipathStore = useMultipathStore();
	const residualsStore = useResidualsStore();
	const paasStddevStore = usePaasStddevStore();
	const mapGssOverlayStore = useMapGssOverlayStore();
	const licenseStore = useLicenseStore();
	mapGssOverlayStore.setVisibility(mode.value === MAP_MODE.main);

	const updateBSData = (): void => {
		baseStationRefreshList();
		moduleStatuses.refresh();
		events.refreshUnreadCount();
		gnssVisibilityStore.refresh();
		gnssPerGridpointStore.refresh();
		snrStore.refresh();
		multipathStore.refresh();
		residualsStore.refresh();
		paasStddevStore.refresh();
		mapGssOverlayStore.refresh();
		licenseStore.refresh(() => {
			if (userStore.allowBaselines) {
				baselinesStore.refresh();
			} else {
				baselinesStore.clear();
			}
		});
		setInterval(() => {
			if (autoUpdatesAllowed.value) {
				baseStationRefreshList();
				moduleStatuses.refresh();
				events.refreshUnreadCount();
				gnssVisibilityStore.refresh();
				gnssPerGridpointStore.refresh();
				snrStore.refresh();
				multipathStore.refresh();
				residualsStore.refresh();
				paasStddevStore.refresh();
				mapGssOverlayStore.refresh();
				if (userStore.allowBaselines) {
					baselinesStore.refresh();
				} else {
					baselinesStore.clear();
				}
			}
		}, 7000);
	};

	const setMode = (modeValue: MapMode): void => {
		mode.value = modeValue;
		gnssVisibilityStore.setVisibility(modeValue === MAP_MODE.gnss);
		gnssPerGridpointStore.setVisibility(modeValue === MAP_MODE.gnssPerGridpoint);
		if (modeValue === MAP_MODE.gnssPerGridpoint) {
			subNetworksStore.refreshCurrentGridpoints();
		}
		snrStore.setVisibility(modeValue === MAP_MODE.snr);
		multipathStore.setVisibility(modeValue === MAP_MODE.multipath);
		residualsStore.setVisibility(isResidualsVisibilityMode(modeValue));
		paasStddevStore.setVisibility(modeValue === MAP_MODE.paasStddev);
		mapGssOverlayStore.setVisibility(modeValue === MAP_MODE.main);
		baseStationRefreshList();
	};

	const clear = (): void => {
		update([], [], [], null, null, []);
		subNetworksStore.clearGridpoints();
		setMode(MAP_MODE.main);
	};

	return {
		mode,
		items,
		rovers,
		maxBaseStationsCount,
		geoFencing,
		profiles,
		isLoading,
		autoUpdatesAllowed,

		mountpointsSelectOptions,
		mountpointsSelectCurrentSubNetworkOptions,

		changeStatuses,
		setAutoUpdates,
		baseStationRefreshList,
		updateBSData,
		setMode,
		clear,
	};
});
